import { useFilters } from '@statsbomb/kitbag-global-football';
import { applyEventFilterGroups } from '@statsbomb/kitbag-global-football/state';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMatch } from 'react-router-dom';
import { minuteMinMaxFilterAtom, xgMinMaxFilterAtom } from '@/atoms/filters/events';
import { selectedPositionsFilterParamsAtom } from '@/atoms/filters/highLevel/positions';
import { selectedPitchFilterCoordsAtom } from '@/atoms/filters/pitchFilter';
import { unwrappedPlayerSelectedGamesForQueryAtom } from '@/atoms/filters/player/playerGames';
import { appliedMappingMetricAtom, mappedMetricReadyDebounceObject } from '@/atoms/metric/metricFilterMapping';
import { obvSelectedAtom } from '@/atoms/queries/eventData';
import { playerEventMetricFiltersDebounceObject } from '@/atoms/queries/player/playerEventData';
import { playerMinutesPlayedFilterDebounceObject } from '@/atoms/queries/player/playerMinsPlayed';
import { playerPositionsFilterDebounceObject } from '@/atoms/queries/player/playerPositions';
import { playerRadarFilterDebounceObject } from '@/atoms/queries/player/playerRadarData';
import { DEFAULT_DEBOUNCE_MS } from '@/consts/api';
import { PLAY_PATTERN_API_FIELD } from '@/consts/playPatterns';
import { optionsToValueStrings, toObject } from '@/utils/object';
import { useDeepEffect } from '../useDeepEffect';

export const useSetPlayerEventDataDebouncedFilterParams = (debounceTimeout: number = DEFAULT_DEBOUNCE_MS) => {
  const [
    {
      eventNames: selectedEventNames,
      outcomes: selectedOutcomes,
      playPatterns: selectedPlayPatterns,
      bodyParts: selectedBodyParts,
      eventFilterGroups: selectedEventFilterGroups,
      matchPeriods: selectedMatchPeriods,
    },
  ] = useFilters('main');
  const playerGameIds = optionsToValueStrings(useAtomValue(unwrappedPlayerSelectedGamesForQueryAtom));
  const selectedPositionsFilterParams = useAtomValue(selectedPositionsFilterParamsAtom);
  const selectedPitchFilterCoords = useAtomValue(selectedPitchFilterCoordsAtom);
  const obvSelected = useAtomValue(obvSelectedAtom);
  const selectedXg = useAtomValue(xgMinMaxFilterAtom);
  const selectedMinute = useAtomValue(minuteMinMaxFilterAtom);

  const { debouncedValueAtom: playerPositionsFiltersAtom, timeoutDelayAtom: playerPositionsFiltersTimeoutDelayAtom } =
    playerPositionsFilterDebounceObject;
  const setPlayerPositionsFiltersTimeoutDelay = useSetAtom(playerPositionsFiltersTimeoutDelayAtom);
  const setPlayerPositionsFilters = useSetAtom(playerPositionsFiltersAtom);

  const { debouncedValueAtom: playerRadarFilterAtom, timeoutDelayAtom: playerRadarFilterTimeoutDelayAtom } =
    playerRadarFilterDebounceObject;
  const setPlayerRadarFilterTimeoutDelay = useSetAtom(playerRadarFilterTimeoutDelayAtom);
  const setPlayerRadarFilter = useSetAtom(playerRadarFilterAtom);

  const {
    debouncedValueAtom: playerEventMetricFiltersAtom,
    timeoutDelayAtom: playerEventMetricFiltersTimeoutDelayAtom,
  } = playerEventMetricFiltersDebounceObject;
  const setPlayerEventMetricFilters = useSetAtom(playerEventMetricFiltersAtom);
  const setPlayerEventMetricFiltersTimeoutDelay = useSetAtom(playerEventMetricFiltersTimeoutDelayAtom);

  const {
    debouncedValueAtom: playerMinutesPlayedFilterAtom,
    timeoutDelayAtom: playerMinutesPlayedFilterTimeoutDelayAtom,
  } = playerMinutesPlayedFilterDebounceObject;
  const setPlayerMinutesPlayedFilter = useSetAtom(playerMinutesPlayedFilterAtom);
  const setPlayerMinutesPlayedFilterTimeoutDelay = useSetAtom(playerMinutesPlayedFilterTimeoutDelayAtom);

  const appliedMappingMetric = useAtomValue(appliedMappingMetricAtom);
  const { debouncedValueAtom: mappedMetricReadyAtom, timeoutDelayAtom: mappedMetricReadyTimeoutDelayAtom } =
    mappedMetricReadyDebounceObject;
  const setMappedMetricReady = useSetAtom(mappedMetricReadyAtom);
  const setMappedMetricReadyTimeoutDelay = useSetAtom(mappedMetricReadyTimeoutDelayAtom);

  const updateTimeouts = (timeout: number) => {
    setMappedMetricReadyTimeoutDelay(timeout);
    setPlayerPositionsFiltersTimeoutDelay(timeout);
    setPlayerRadarFilterTimeoutDelay(timeout);
    setPlayerEventMetricFiltersTimeoutDelay(timeout);
    setPlayerMinutesPlayedFilterTimeoutDelay(timeout);
  };

  // we want the event filters to apply only when on the player vis page/tab
  const isPlayerVisPage = !!useMatch('/:locale/player/:playerId/vis/*');
  const visOnlyEventFilters = isPlayerVisPage
    ? {
        detail: {
          ...toObject('event.type', selectedEventNames),
          ...toObject('event.attributes.outcome', selectedOutcomes),
          ...toObject(PLAY_PATTERN_API_FIELD, selectedPlayPatterns),
          ...toObject('event.attributes.body_part', selectedBodyParts),
          ...toObject('event.period', selectedMatchPeriods),
        },
        groups: selectedEventFilterGroups,
        pitch: selectedPitchFilterCoords,
        gte: { ...obvSelected.min, ...selectedXg.min, ...selectedMinute.min },
        lte: { ...obvSelected.max, ...selectedXg.max, ...selectedMinute.max },
      }
    : { detail: {}, groups: [], pitch: {}, gte: {}, lte: {} };

  useDeepEffect(() => {
    // If there's no games, set the timeout to 0 as this won't trigger an API call.
    if (!playerGameIds.length) updateTimeouts(0);
    setPlayerPositionsFilters({
      in: { game_id: playerGameIds },
    });
    const { detail, groups, pitch, gte, lte } = visOnlyEventFilters;
    const baseEventMetricFilter = {
      in: {
        ...toObject('event.attributes.player_position', selectedPositionsFilterParams),
        ...detail,
        'event.game_id': playerGameIds,
      },
      inside: pitch,
      gte,
      lte,
    };
    setPlayerEventMetricFilters(applyEventFilterGroups(baseEventMetricFilter, groups));
    setMappedMetricReady(appliedMappingMetric);
    setPlayerRadarFilter({
      in: {
        ...toObject('position', selectedPositionsFilterParams),
        game_id: playerGameIds,
      },
    });
    setPlayerMinutesPlayedFilter({
      in: { 'input.game_id': playerGameIds },
    });
    // If there were games this time, set the timeout so that we debounce subsequent calls
    if (playerGameIds.length) updateTimeouts(debounceTimeout);
  }, [playerGameIds, selectedPositionsFilterParams, visOnlyEventFilters, appliedMappingMetric]);
};
